import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, Table, Tag, Button, Modal, List, Typography, Space, DatePicker, Row, Col, Statistic, message } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import Filter from '../../../components/filter/Filter';
import axiosInstance from '../../../services/axiosServicesVisit';
import { getDelegatesList } from "../../../redux/slices/delegates/delegatesThunk";
import { getProducts } from "../../../redux/slices/products/productsThunk";
import { getSpecialtiesForFilter } from "../../../redux/slices/specialtys/specialtysThunk";
import { getRegions } from "../../../redux/slices/regions/regionsThunk";
import dayjs from 'dayjs';
import { 
  UserOutlined, 
  EnvironmentOutlined, 
  TeamOutlined, 
  CheckCircleOutlined, 
  ShoppingOutlined,
  GlobalOutlined,
  DownloadOutlined 
} from '@ant-design/icons';

const { RangePicker } = DatePicker;
const { Text } = Typography;

const ProductSummaryPage = () => {
  const dispatch = useDispatch();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  
  // État pour l'AFFICHAGE (contient les noms pour les tags)
  const [filters, setFilters] = useState({ 
    delegateIds: [], 
    productIds: [],
    specialtyIds: [],
    regionIds: [],
    dateInterval: [dayjs().startOf('month').format('YYYY-MM-DD'), dayjs().format('YYYY-MM-DD')] 
  });

  // État pour l'API (contient les IDs réels)
  const [filterIds, setFilterIds] = useState({
    delegateIds: [],
    productIds: [],
    specialtyIds: [],
    regionIds: []
  });

  const [modalVisible, setModalVisible] = useState(false);
  const [selectedDoctors, setSelectedDoctors] = useState([]);
  const [selectedItemName, setSelectedItemName] = useState('');

  const delegatesList = useSelector((state) => state.delegates?.delegatesList?.data?.data || []);
  const productsList = useSelector((state) => state.products?.products?.data || []);
  const specialtiesList = useSelector((state) => state.specialtys?.specialtiesList || []);
  const regionsList = useSelector((state) => state.regions?.regions?.data || []);

  useEffect(() => {
    dispatch(getDelegatesList({ page: 1, itemsPerPage: 1000 }));
    dispatch(getProducts({ page: 1, limit: 1000, filter: {} }));
    dispatch(getSpecialtiesForFilter());
    dispatch(getRegions({ page: 1, itemsPerPage: 1000 }));
  }, [dispatch]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      // On utilise filterIds pour l'appel API
      Object.keys(filterIds).forEach(key => {
          if (filterIds[key]?.length > 0) params.append(key, filterIds[key].join(','));
      });
      // Ajout de la date
      if (filters.dateInterval?.length === 2) {
          params.append('dateInterval', filters.dateInterval.join(','));
      }
      
      const res = await axiosInstance.get(`/v1/reports/summary/product-activity?${params.toString()}`);
      setData(res.data);
    } catch (error) {
      console.error("Erreur chargement", error);
    } finally {
      setLoading(false);
    }
  }, [filterIds, filters.dateInterval]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const stats = useMemo(() => {
    const totalPresentations = data.reduce((acc, curr) => acc + curr.visitCount, 0);
    const uniqueDelegates = new Set(data.map(item => item.delegateId)).size;
    const uniqueProducts = new Set(data.map(item => item.productId)).size;
    const uniqueRegions = new Set(data.flatMap(item => item.regions)).size;
    return { totalPresentations, uniqueDelegates, uniqueProducts, uniqueRegions };
  }, [data]);

  // --- GESTION DU FILTRE CORRIGÉE ---
  const handleFilterChange = (value, key, id) => {
    // 1. Mettre à jour les noms pour l'affichage des tags
    setFilters(prev => ({ ...prev, [key]: value }));

    // 2. Mettre à jour les IDs pour l'API (si 'id' existe, sinon on prend 'value')
    const actualIds = (id && id.length > 0) ? id : value;
    setFilterIds(prev => ({ ...prev, [key]: actualIds }));
  };

  const handleDateChange = (dates) => {
    const dateArr = dates ? [dates[0].format('YYYY-MM-DD'), dates[1].format('YYYY-MM-DD')] : [];
    setFilters(prev => ({ ...prev, dateInterval: dateArr }));
  };

  const resetFilters = () => {
    const defaultDate = [dayjs().startOf('month').format('YYYY-MM-DD'), dayjs().format('YYYY-MM-DD')];
    setFilters({ delegateIds: [], productIds: [], specialtyIds: [], regionIds: [], dateInterval: defaultDate });
    setFilterIds({ delegateIds: [], productIds: [], specialtyIds: [], regionIds: [] });
  };

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      Object.keys(filterIds).forEach(key => {
          if (filterIds[key]?.length > 0) params.append(key, filterIds[key].join(','));
      });
      if (filters.dateInterval?.length === 2) {
          params.append('dateInterval', filters.dateInterval.join(','));
      }

      // Appel direct à l'URL d'export
      const response = await axiosInstance.get(`/v1/reports/summary/product-activity/export?${params.toString()}`, {
        responseType: 'blob',
      });

      // Téléchargement du fichier
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Activite_Produits_${dayjs().format('YYYY-MM-DD')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      message.success("Exportation réussie.");
    } catch (error) {
      console.error("Erreur export", error);
      message.error("Erreur lors de l'exportation.");
    } finally {
      setExporting(false);
    }
  };

  const optionsLists = [
    { key: "productIds", placeholder: "Filtrer par Produit", multiple: true, options: productsList.map(p => ({ id: p.id, label: p.name, value: p.name })), showSearch: true },
    { key: "delegateIds", placeholder: "Filtrer par Délégué", multiple: true, options: delegatesList.map(d => ({ id: d.id, label: `${d.firstname} ${d.lastname}`.toUpperCase(), value: `${d.firstname} ${d.lastname}`.toUpperCase() })), showSearch: true },
    { key: "specialtyIds", placeholder: "Filtrer par Spécialité", multiple: true, options: specialtiesList.map(s => ({ id: s.id, label: s.name, value: s.name })), showSearch: true },
    { key: "regionIds", placeholder: "Filtrer par Région", multiple: true, options: regionsList.map(r => ({ id: r.id, label: r.name, value: r.name })), showSearch: true }
  ];

  const columns = [
    {
      title: 'Produit Présenté',
      dataIndex: 'productName',
      key: 'productName',
      render: (text) => <Text strong style={{ color: '#1890ff' }}>{text}</Text>
    },
    {
      title: 'Délégué',
      dataIndex: 'delegateName',
      key: 'delegateName',
      render: (text) => <Text><TeamOutlined /> {text}</Text>,
    },
    {
      title: 'Visites',
      dataIndex: 'visitCount',
      key: 'visitCount',
      align: 'center',
      render: (count, record) => (
        <Button type="primary" ghost size="small" onClick={() => {
          setSelectedDoctors(record.doctors);
          setSelectedItemName(`${record.productName} (${record.delegateName})`);
          setModalVisible(true);
        }}>
          {count} visites
        </Button>
      )
    },
    {
      title: 'Régions Atteintes',
      dataIndex: 'regions',
      key: 'regions',
      render: (regions) => regions?.map(r => <Tag color="blue" key={r} style={{fontSize:'10px'}}>{r}</Tag>)
    },
    {
      title: 'Spécialités ciblées',
      dataIndex: 'specialties',
      key: 'specialties',
      render: (specs) => specs?.map(s => <Tag color="purple" key={s} style={{fontSize:'10px'}}>{s}</Tag>)
    }
  ];

  return (
    <div className="tabled">
      <Row gutter={[24, 24]}>
        <Col xs={24}>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={6}><Card bordered={false} className="criclebox"><Statistic title="Total Présentations" value={stats.totalPresentations} prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />} valueStyle={{ color: '#52c41a', fontWeight: 'bold' }} /></Card></Col>
            <Col xs={24} sm={6}><Card bordered={false} className="criclebox"><Statistic title="Délégués Actifs" value={stats.uniqueDelegates} prefix={<TeamOutlined style={{ color: '#1890ff' }} />} valueStyle={{ color: '#1890ff', fontWeight: 'bold' }} /></Card></Col>
            <Col xs={24} sm={6}><Card bordered={false} className="criclebox"><Statistic title="Produits Catalogués" value={stats.uniqueProducts} prefix={<ShoppingOutlined style={{ color: '#faad14' }} />} valueStyle={{ color: '#faad14', fontWeight: 'bold' }} /></Card></Col>
            <Col xs={24} sm={6}><Card bordered={false} className="criclebox"><Statistic title="Régions Couvertes" value={stats.uniqueRegions} prefix={<GlobalOutlined style={{ color: '#722ed1' }} />} valueStyle={{ color: '#722ed1', fontWeight: 'bold' }} /></Card></Col>
          </Row>
        </Col>

        <Col xs={24}>
          <Card title="Détail de l'activité par Produit" bordered={false} className="criclebox tablespace mb-24">
            <div style={{ padding: '20px' }}>
                <Row gutter={[16, 16]} align="middle">
                  <Col span={24}>
                    <Space size="middle" wrap>
                      <Text strong>Période : </Text>
                      <RangePicker 
                        value={[dayjs(filters.dateInterval[0]), dayjs(filters.dateInterval[1])]}
                        onChange={handleDateChange} 
                      />
                      <Button onClick={resetFilters}>Réinitialiser</Button>
                      <Button 
                        icon={<DownloadOutlined />} 
                        onClick={handleExportCSV} 
                        loading={exporting}
                        type="primary"
                        ghost
                      >
                        Exporter
                      </Button>
                    </Space>
                  </Col>
                  <Col span={24}>
                    <Filter optionsLists={optionsLists} onSelect={handleFilterChange} data={filters} />
                  </Col>
                </Row>
            </div>
            <Table 
              columns={columns} 
              dataSource={data} 
              loading={loading} 
              rowKey={(record) => `${record.delegateId}-${record.productId}`}
              rowClassName={(record, index) => index % 2 === 0 ? 'table-row-light' : 'table-row-dark'}
            />
          </Card>
        </Col>
      </Row>

      <Modal
        title={<span>Détails : <b>{selectedItemName}</b></span>}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={800}
      >
        <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
            <List
              itemLayout="horizontal"
              dataSource={selectedDoctors}
              renderItem={(item) => (
                <List.Item>
                  <List.Item.Meta
                    avatar={<UserOutlined />}
                    title={<Text strong>{item.name}</Text>}
                    description={
                      <Space split={<span>|</span>}>
                        <Tag color="cyan">{item.specialty}</Tag>
                        <span><EnvironmentOutlined /> Visité le : <b>{dayjs(item.date).format('DD/MM/YYYY')}</b></span>
                      </Space>
                    }
                  />
                </List.Item>
              )}
            />
        </div>
      </Modal>
    </div>
  );
};

export default ProductSummaryPage;